/* eslint-disable react/sort-comp */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {} from 'mapbox-gl-leaflet';
import 'leaflet-fullscreen';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import styles from './attachments.module.scss';
import 'leaflet-polylinedecorator';
import 'leaflet.markercluster/dist/leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.gridlayer.googlemutant/dist/Leaflet.GoogleMutant';
import { CUSTOM_MAP, custom_map_max_zoom_level, get_custom_map_url, MAP_BOX, NEAR_MAP } from '../../constants/mapSettings';
import icon from '../../icons/formMarker.png';
import { uniqueID, getDistanceFromLatLonInKm } from '../../utils/helpers';

class MapLocationDetail extends Component {
  constructor(props) {
    super(props);
    this.mapContainerRef = React.createRef();
  }

  defaultConfig = {
    marker: false,
    circle: false,
    rectangle: false,
    polyline: false,
    circlemarker: false,
    polygon: false
  };

  ID = uniqueID('map');

  async componentDidMount() {
    const { user } = this.props;
    const airportLocation = user?.airport?.location || user?.portal_airport?.location;
    if (airportLocation && Object.keys(airportLocation).length > 0) {
      this.initializeMap();
      this.observeMapContainer();
    }
  }

  componentWillUnmount() {
    if (this.invalidateTimeouts?.length) {
      this.invalidateTimeouts.forEach(clearTimeout);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.invalidateFrame) {
      cancelAnimationFrame(this.invalidateFrame);
    }
    if (this.map) this.map.remove();
  }

  observeMapContainer = () => {
    if (!this.mapContainerRef.current || typeof ResizeObserver === 'undefined') {
      this.invalidateMapSize();
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.invalidateMapSize();
    });

    this.resizeObserver.observe(this.mapContainerRef.current);
    this.invalidateMapSize();
  };

  invalidateMapSize = () => {
    if (this.invalidateFrame) {
      cancelAnimationFrame(this.invalidateFrame);
    }
    if (this.invalidateTimeouts?.length) {
      this.invalidateTimeouts.forEach(clearTimeout);
    }

    this.invalidateFrame = requestAnimationFrame(() => {
      if (this.map) {
        this.map.invalidateSize(true);
      }
    });

    this.invalidateTimeouts = [100, 300, 700, 1200].map(delay => setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize(true);
      }
    }, delay));
  };

  initializeMap = () => {
    const {
      location,
      user,
      shape,
      customIcon,
      multiple_location_point
    } = this.props;
    const airport = user?.airport || user?.portal_airport;
    const airportSettings = user?.airport_settings || user?.portal_airport?.airport_settings;
    const defaultLocation = airport?.location?.coordinates;
    if (
      !Array.isArray(defaultLocation)
      || defaultLocation.length < 2
    ) {
      return;
    }
    const curLocation = [defaultLocation[1], defaultLocation[0]];
    this.map = L.map(this.mapContainerRef.current, {
      center: curLocation,
      zoom: 15,
      fullscreenControl: true,
      attributionControl: false
    });

    if (airportSettings?.map_style === MAP_BOX) {
      L.mapboxGL({
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN,
        style: `${
          airportSettings?.mapbox_style
            ? airportSettings.mapbox_style
            : 'mapbox://styles/mapbox/satellite-v9'
        }`
      }).addTo(this.map);
    } else if (airportSettings?.map_style === NEAR_MAP) {
      L.tileLayer(
        `https://api.nearmap.com/tiles/v3/Vert/{z}/{x}/{y}.jpg?tertiary=satellite&apikey=${airportSettings.nearmap_apikey}`
      ).addTo(this.map);
    } else if (airportSettings?.map_style === CUSTOM_MAP) {
      L.tileLayer(get_custom_map_url(airportSettings?.custom_map_url), {
        maxZoom: custom_map_max_zoom_level
      }).addTo(this.map);
    } else {
      L.gridLayer
        .googleMutant({ type: 'satellite', maxZoom: 20 })
        .addTo(this.map);
    }
    if (multiple_location_point?.length) {
      multiple_location_point.forEach((e) => {
        const dis = getDistanceFromLatLonInKm(
          user.airport.location.coordinates[1],
          user.airport.location.coordinates[0],
          e?.coordinates[0],
          e?.coordinates[1]
        );
        let coordinates = null;
        if (e === null) {
          coordinates = undefined;
        } else if (dis < 1000) {
          coordinates = [e?.coordinates[0], e?.coordinates[1]];
        } else {
          coordinates = [e?.coordinates[1], e?.coordinates[0]];
        }
        if (shape === 'Point' && e?.type === 'Point') {
          const marker = new L.Marker(coordinates, {
            icon: new L.Icon({
              iconUrl: customIcon ?? icon,
              iconAnchor: [25, 35]
            })
          });
          this.map.addLayer(marker);
          this.map.setView([...coordinates], 18);
        }
      });
    }
    if (location?.coordinates && location?.coordinates.length > 0) {
      if (shape === 'Point' && location?.type === 'Point') {
        const marker = new L.Marker(location.coordinates, {
          icon: new L.Icon({
            iconUrl: customIcon ?? icon,
            iconAnchor: [25, 35]
          })
        });
        this.map.addLayer(marker);
        this.map.setView([...location.coordinates], 18);
      }
    }
    if (location && location.coordinates && location.coordinates.length > 0) {
      if (shape === 'Point' && location?.type === 'Point') {
        const marker = new L.Marker(location.coordinates, {
          icon: new L.Icon({
            iconUrl: customIcon ?? icon,
            iconAnchor: [25, 35]
          })
        });
        this.map.addLayer(marker);
        this.map.setView([...location.coordinates], 18);
      }
      if (shape === 'Polygon' && location?.type === 'Polygon') {
        const latlang = [];
        location.coordinates[0].forEach((e) => {
          latlang.push([e[1], e[0]]);
        });
        const polygon = L.polygon(latlang).addTo(this.map);
        this.map.addLayer(polygon);
        this.map.fitBounds(polygon.getBounds());
      }
      if (shape === 'LineString' && location?.type === 'LineString') {
        const latlang = [];
        location.coordinates.forEach((e) => {
          latlang.push([e[1], e[0]]);
        });
        const polyline = L.polyline(latlang).addTo(this.map);
        this.map.addLayer(polyline);
        this.map.fitBounds(polyline.getBounds());
      }
    }
  };

  render() {
    const { printMap } = this.props;
    return (
      <div
        id={this.ID}
        ref={this.mapContainerRef}
        className={styles.map}
        style={printMap ? {
          width: '1000px',
          minWidth: '1000px',
          maxWidth: '100%',
          height: '340px'
        } : undefined}
      />
    );
  }
}

MapLocationDetail.propTypes = {
  location: PropTypes.arrayOf(PropTypes.shape({}))
};

MapLocationDetail.defaultProps = {
  location: []
};

const mapStateToProps = state => ({
  user: state.auth.profile
});

export default connect(mapStateToProps, {})(MapLocationDetail);
